/**
 * Routes that render for a visitor with no session, and must never be
 * interrupted by an auth redirect.
 *
 * Why this file exists
 * --------------------
 * The auth layer already had this concept — it was just written inline, twice,
 * as `pathname.includes('/firebase-login')` and `pathname.includes('/onboarding')`,
 * copied across six guard sites in AuthContext.tsx and api.service.js. App.jsx
 * declares EIGHT routes as "PUBLIC ROUTE"; the guards knew about two of them.
 *
 * On 2026-09-04 a customer of 11Solutions opened the signed link for invoice
 * CLU-2026-028 in a private window. The page rendered — the invoice, the amount,
 * the Pay button — and then, a second later, the app threw him at
 * /firebase-login?session-expired=true. He has no GRID account; the page exists
 * precisely for that.
 *
 * The lesson that produced this module: guarding the ENDPOINT is not enough.
 * An earlier fix exempted `billing/v1/public/` from api.service's redirect
 * handlers, and the bounce continued, because the redirect came from a
 * DIFFERENT call made while sitting on the public page — AuthContext's own
 * session machinery, which never consulted the endpoint at all. What has to be
 * guarded is the PAGE: if the user is on a route that renders without a
 * session, no failure to establish a session may navigate them away from it.
 *
 * Keep this list in sync with the routes App.jsx marks "PUBLIC ROUTE".
 */

export const PUBLIC_ROUTE_PREFIXES = [
  '/firebase-login',   // the login screen itself
  '/onboarding',       // public onboarding wizard (API-key auth, no user)
  '/i/',               // signed public invoice page — BILLING_COBRANZA_TAREAS.md PASO 6
  '/pub/',             // published Poiesis modules
  '/products/',        // ISO product landing + start, CofounderBuddy redirect to /s/
  '/m/products/',      // mobile CofounderBuddy redirect to /s/ (mounted outside the /m ProtectedRoute block)
  '/marketplace',      // ISO public marketplace
  '/flows/',           // flow runner (public exposure mode)
  '/grid/',            // FBT app host
  '/store/',           // Quorra public storefront and its shareable product links. The
                       // store admin is /x/commerce, which stays behind ProtectedRoute.
];

/**
 * Routes where a GRID session must NOT be established — a strict subset of the
 * list above, and a DIFFERENT question from it.
 *
 * "Never navigate away from this page" and "never try to establish a session
 * here" look like the same predicate and are not. `/firebase-login` answers
 * yes to the first and NO to the second: it is the one page whose entire job
 * is to establish the session. Reusing `isPublicRoute` for both on 2026-09-04
 * made AuthContext skip `auth/gateway` while sitting on the login screen, so
 * every sign-in produced a Firebase user with no GRID role — and
 * ProtectedRoute then bounced it straight back to `/firebase-login?access-denied=true`.
 * A login loop for every user on the platform.
 *
 * Only `/onboarding` belongs here: the visitor has authenticated with Firebase
 * but does not exist in `bt4sap.users` yet (RegisterStep calls
 * `/api/public/onboarding/register` to create them), so the gateway would fail
 * by definition. Every other public page either has no Firebase user at all —
 * in which case this branch is never reached — or has a real session that
 * should be loaded normally.
 */
export const NO_SESSION_ESTABLISHMENT_PREFIXES = ['/onboarding'];

function matches(prefixes, pathname) {
  let path = pathname;
  if (path === undefined) {
    if (typeof window === 'undefined') return false;
    path = window.location.pathname;
  }
  if (!path) return false;
  return prefixes.some((prefix) => path.startsWith(prefix));
}

/**
 * True when the given path (default: the current location) renders without a
 * session and must never be navigated away from by an auth failure. Safe to
 * call outside a browser — returns false when there is no `window`, so
 * server/test contexts never accidentally suppress a redirect.
 */
export function isPublicRoute(pathname) {
  return matches(PUBLIC_ROUTE_PREFIXES, pathname);
}

/**
 * True when the auth gateway must NOT be called for the given path. Use this —
 * never `isPublicRoute` — to decide whether to establish a session.
 */
export function skipsAuthGateway(pathname) {
  return matches(NO_SESSION_ESTABLISHMENT_PREFIXES, pathname);
}

export default isPublicRoute;
